import { ConsentCheckboxId } from './checkboxes';
import { SettingsToggleId } from './settings-toggles';

// A variant is one whole consent experience: which checkboxes the modal shows,
// which toggles Settings then shows, and which legal copy goes with them.
// GrowthBook picks one per user from their country and the consent they already
// hold, so the four named below are the four cases the product asked for, plus
// the pre-existing screen everyone still sees until this is ramped up.

export type ConsentVariantId =
  | 'us-cs-held'
  | 'us-cs-missing'
  | 'row'
  | 'row-cs-held'
  | 'legacy-unified';

export interface IConsentVariant {
  checkboxes: ConsentCheckboxId[];
  settingsToggles: SettingsToggleId[];
  legalTextKey: string;
  // Sentence between the legal box and the checkboxes. First-run RoW only.
  checkboxPromptKey?: string;
  showConsentToAll: boolean;
  // legacy-unified only: keep the old Settings behaviour, where a toggle is
  // disabled while cs is off, instead of opening the consent modal.
  useLegacySettingsInteraction: boolean;
  // Keep the terms checkbox on screen even after eulaAgreed. Independent of
  // useLegacySettingsInteraction so a future variant can reuse one without the other.
  alwaysShowTerms: boolean;
}

export const CONSENT_VARIANTS: Record<ConsentVariantId, IConsentVariant> = {
  // GrowthBook targets this on `country = US AND optInPreferences includes cs`.
  // Deliberately not on eulaAgreed: a user can accept the terms without ticking
  // any data checkbox, so eulaAgreed would send someone holding no cs to a screen
  // where nothing can grant it.
  'us-cs-held': {
    checkboxes: ['ai', 'tc-us-held'],
    settingsToggles: ['all'],
    legalTextKey: 'onboarding.dataConsent.legalTextUsAi',
    showConsentToAll: false,
    useLegacySettingsInteraction: false,
    alwaysShowTerms: false,
  },
  'us-cs-missing': {
    checkboxes: ['us-all-data', 'tc-us'],
    settingsToggles: ['all'],
    legalTextKey: 'onboarding.dataConsent.legalTextUs',
    showConsentToAll: false,
    useLegacySettingsInteraction: false,
    alwaysShowTerms: false,
  },
  row: {
    checkboxes: ['affiliate', 'thirdParty', 'tc-row'],
    settingsToggles: ['all'],
    legalTextKey: 'onboarding.dataConsent.legalTextRow',
    checkboxPromptKey: 'onboarding.dataConsent.checkboxPrompt',
    showConsentToAll: true,
    useLegacySettingsInteraction: false,
    alwaysShowTerms: false,
  },
  // Returning RoW users already hold cs, which is what tc-row grants, so this
  // screen does not ask for terms again. Both disclosure checkboxes stay listed
  // so resolveVisibleCheckboxes can hide only the ones already accepted — listing
  // half the all-or-nothing group would let a single tick grant the whole group.
  'row-cs-held': {
    checkboxes: ['affiliate', 'thirdParty'],
    settingsToggles: ['all'],
    legalTextKey: 'onboarding.dataConsent.legalTextRowHeld',
    showConsentToAll: false,
    useLegacySettingsInteraction: false,
    alwaysShowTerms: false,
  },
  'legacy-unified': {
    checkboxes: ['all-buckets-legacy', 'tc-legacy'],
    settingsToggles: ['all'],
    legalTextKey: 'onboarding.dataConsent.legalText',
    showConsentToAll: true,
    useLegacySettingsInteraction: true,
    alwaysShowTerms: true,
  },
};

// Falling back to the pre-existing screen makes the default a safe no-op: a user
// not yet ramped up, and a GrowthBook outage, both simply see what shipped before.
// The other four are only ever reached through GrowthBook targeting.
export const DEFAULT_CONSENT_VARIANT_ID: ConsentVariantId = 'legacy-unified';

// hasOwnProperty rather than `in`, because `in` walks the prototype chain: "toString"
// or "constructor" would pass the check and then resolve to a function.
export const isConsentVariantId = (value: unknown): value is ConsentVariantId =>
  typeof value === 'string' &&
  Object.prototype.hasOwnProperty.call(CONSENT_VARIANTS, value);
